import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  ButtonWithGAClickButton,
  LinkWithGAClickButton,
  Panel,
  Section,
  SectionSubTitle,
  Typography,
} from 'common/components';
import { analyticsUtils } from 'utils/analytics-utils';
import { BATHROOM_REPAIR_ELEMENT_BLOCK } from 'landings/bathroom-repair/bathroom-repair-constants';
import { BATHROOM_ELEMENT_BLOCK } from 'landings/bathroom/bathroom-constants';
import {
  questionAccordion,
  questionList,
  questionListItem,
  questionSignButton,
  questionSignLink,
  questionText,
} from './bathroom-repair-question-section-styles';

type BathroomRepairQuestionSectionProps = {
  designSignHref: string;
};

export const BathroomRepairQuestionSection: React.FC<BathroomRepairQuestionSectionProps> = ({
  designSignHref,
}) => {
  const { t } = useTranslation();

  /**
   * Обработчик нажатия на колапс панели
   */
  const handlePanelClick = (question: string, order: number) => (_: string, isOpen: boolean) => {
    if (!isOpen) {
      analyticsUtils.clickButtonEvent(
        BATHROOM_REPAIR_ELEMENT_BLOCK,
        `${question.slice(0, 100)} / ${order}`,
        t('question-section.title')
      );
    }
  };

  return (
    <Section itemScope itemType="https://schema.org/FAQPage">
      <SectionSubTitle margin="m">{t('question-section.title')}</SectionSubTitle>
      <Accordion className={questionAccordion}>
        <Panel
          name="pricing"
          onClick={handlePanelClick(t('question-section.pricing.question'), 1)}
          title={t('question-section.pricing.question')}>
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long" className={questionText}>
                {t('question-section.pricing.answer1')}
              </Typography>
              <ol className={questionList}>
                <li className={questionListItem}>
                  <Typography variant="s" size="body_long">
                    {t('question-section.pricing.answer2-part1')}{' '}
                    <LinkWithGAClickButton
                      className={questionSignLink}
                      elementBlock={`${BATHROOM_ELEMENT_BLOCK} / faq`}
                      elementName={t('question-section.pricing.question')}
                      elementText={t('question-section.pricing.answer2-link')}
                      title={t('design-panel.button')}
                      href={designSignHref}
                      target="_blank">
                      {t('question-section.pricing.answer2-link')}
                    </LinkWithGAClickButton>{' '}
                    {t('question-section.pricing.answer2-part2')}
                    <br />
                    <LinkWithGAClickButton
                      className={questionSignLink}
                      elementBlock={`${BATHROOM_ELEMENT_BLOCK} / faq`}
                      elementName={t('question-section.pricing.question')}
                      elementText={t('question-section.pricing.link-title')}
                      title={t('question-section.pricing.link-title')}
                      href="/advice/santehnika/kak-izmerit-vannuyu-komnatu/">
                      {t('question-section.pricing.link-title')}
                    </LinkWithGAClickButton>
                  </Typography>
                </li>
                <li className={questionListItem}>
                  <Typography variant="s" size="body_long">
                    {t('question-section.pricing.answer3')}
                  </Typography>
                </li>
              </ol>
              <Typography variant="s" size="body_long" className={questionText}>
                {t('question-section.pricing.answer4')}
              </Typography>
              <ButtonWithGAClickButton
                className={questionSignButton}
                elementBlock={`${BATHROOM_ELEMENT_BLOCK} / faq`}
                elementName={t('question-section.pricing.question')}
                elementText={t('design-panel.button')}
                title={t('design-panel.button') || ''}
                href={designSignHref}
                target="_blank">
                {t('design-panel.button')}
              </ButtonWithGAClickButton>
            </div>
          </div>
        </Panel>
        <Panel
          name="timing"
          onClick={handlePanelClick(t('question-section.timing.question'), 2)}
          title={t('question-section.timing.question')}>
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <Typography variant="s" size="body_long" className={questionText} itemProp="text">
              {t('question-section.timing.answer')}
            </Typography>
          </div>
        </Panel>
        <Panel
          name="always"
          onClick={handlePanelClick(t('question-section.always.question'), 3)}
          title={t('question-section.always.question')}>
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <Typography variant="s" size="body_long" className={questionText} itemProp="text">
              {t('question-section.always.answer')}
            </Typography>
          </div>
        </Panel>
        <Panel
          name="moreGoods"
          onClick={handlePanelClick(t('question-section.more-goods.question'), 4)}
          title={t('question-section.more-goods.question')}>
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <Typography variant="s" size="body_long" className={questionText} itemProp="text">
              {t('question-section.more-goods.answer')}
            </Typography>
          </div>
        </Panel>
        <Panel
          name="experience"
          onClick={handlePanelClick(t('question-section.experience.question'), 5)}
          title={t('question-section.experience.question')}>
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <Typography variant="s" size="body_long" className={questionText} itemProp="text">
              {t('question-section.experience.answer')}
            </Typography>
            <ButtonWithGAClickButton
              className={questionSignButton}
              elementBlock={`${BATHROOM_ELEMENT_BLOCK} / faq`}
              elementName={t('question-section.experience.question')}
              elementText={t('design-panel.button')}
              title={t('design-panel.button') || ''}
              href={designSignHref}
              target="_blank">
              {t('design-panel.button')}
            </ButtonWithGAClickButton>
          </div>
        </Panel>
      </Accordion>
    </Section>
  );
};
